"use client";

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useExamScore } from "@/components/ExamScoreContext";
import {
  ExamScoreSummaryPanel,
  getExamScorePercentage,
  getScoreGamjaImage,
} from "@/components/ExamScoreModal";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";

/**
 * 우측 사이드바 점수 패널 (JCG-2066).
 *
 * 점수는 이론/코딩 글에는 없고 기출·실전퀴즈·감자퀴즈·왕감자퀴즈에서만 노출되던 기능이라,
 * 이 컴포넌트는 ExamScoreProvider(=RandomizedProblems) 안쪽에서만 마운트된다.
 * 셸(데스크탑 글 페이지)이면 마운트 시 점수 Section 을 켜고(setScoreActive) 세션 정답률을
 * scoreSlot 에 createPortal 로 그린다. 셸이 아니면(모바일·비셸) 아무것도 렌더하지 않고
 * 모바일 dock/비셸 플로팅(ExamScoreModal)이 점수를 보여준다.
 *
 * 로그인 불문: ExamScoreContext 는 세션 기반(이번 세션에서 푼 정답/시도 수)이라
 * 비로그인 사용자에게도 동일하게 동작한다.
 */
const ArticleScorePanel: React.FC = () => {
  const examScore = useExamScore();
  const { inShell, scoreSlot, setScoreActive } = useArticleShellSlots();

  // 셸 안에서만 점수 Section 노출. 마운트 시 켜고 언마운트 시 끈다(다른 페이지로 이동 시 사라짐).
  useEffect(() => {
    if (!inShell) return;
    setScoreActive(true);
    return () => setScoreActive(false);
  }, [inShell, setScoreActive]);

  if (!inShell || !scoreSlot || !examScore) return null;

  const { correctCount, totalAttempted } = examScore;
  const percentage = getExamScorePercentage(correctCount, totalAttempted);
  const currentImage = getScoreGamjaImage(percentage, totalAttempted);

  return createPortal(
    <ExamScoreSummaryPanel
      correctCount={correctCount}
      totalAttempted={totalAttempted}
      percentage={percentage}
      currentImage={currentImage}
      className="pt-1 pb-0.5"
      imageClassName="h-16 w-16 shadow-none"
    />,
    scoreSlot,
  );
};

ArticleScorePanel.displayName = "ArticleScorePanel";

export default ArticleScorePanel;
